import { createRoot, type Root } from 'react-dom/client';
import { createElement } from 'react';
import { StudioApp } from './StudioApp';
import type { StudioPorts, StudioView } from './types';
import cssText from './styles.css?inline';

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ox-studio-styles')) return;
  const style = document.createElement('style');
  style.id = 'ox-studio-styles';
  style.textContent = cssText;
  document.head.appendChild(style);
}

class OxStudioElement extends HTMLElement {
  static get observedAttributes() {
    return [
      'studio-api',
      'builder-api',
      'designer-script',
      'designer-accent',
      'designer-on-accent',
      'designer-highlight',
      'template-id',
      'view',
      'adapter',
      'shop-url',
      'designer-page-url',
    ];
  }

  #root: Root | null = null;
  #ports: StudioPorts | null = null;

  connectedCallback() {
    ensureStyles();
    if (!this.#root) {
      const mount = document.createElement('div');
      mount.style.minHeight = '100%';
      this.appendChild(mount);
      this.#root = createRoot(mount);
    }
    this.render();
  }

  disconnectedCallback() {
    this.#root?.unmount();
    this.#root = null;
  }

  attributeChangedCallback() {
    this.render();
  }

  setPorts(ports: StudioPorts) {
    this.#ports = ports;
    this.render();
  }

  private render() {
    if (!this.#root) return;
    const studioApiBase = this.getAttribute('studio-api') || 'http://localhost:4030';
    const builderApiBase = this.getAttribute('builder-api') || 'http://localhost:4010';
    const designerScriptUrl = this.getAttribute('designer-script') || undefined;
    const initialTemplateId = this.getAttribute('template-id') || undefined;
    const viewAttr = (this.getAttribute('view') || 'list') as StudioView;
    const adapter = this.getAttribute('adapter') || 'woo';
    const shopUrl = this.getAttribute('shop-url') || undefined;
    const designerPageUrl = this.getAttribute('designer-page-url') || undefined;

    if (adapter !== 'memory' && !this.#ports) {
      this.#root.render(
        createElement(
          'div',
          {
            style: {
              display: 'grid',
              placeItems: 'center',
              minHeight: 200,
              fontFamily: 'system-ui, sans-serif',
              color: '#555',
            },
          },
          'Cargando Ox Studio…',
        ),
      );
      return;
    }

    if (!this.#ports) {
      this.#root.render(
        createElement('div', null, 'Configura los ports de Ox Studio.'),
      );
      return;
    }

    this.#root.render(
      createElement(StudioApp, {
        ports: this.#ports,
        studioApiBase,
        builderApiBase,
        designerScriptUrl,
        designerAccent: this.getAttribute('designer-accent') || undefined,
        designerOnAccent: this.getAttribute('designer-on-accent') || undefined,
        designerHighlight: this.getAttribute('designer-highlight') || undefined,
        initialTemplateId,
        initialView: viewAttr === 'wizard' ? 'wizard' : 'list',
        shopUrl,
        designerPageUrl,
      }),
    );
  }
}

if (!customElements.get('ox-studio')) {
  customElements.define('ox-studio', OxStudioElement);
}

export { OxStudioElement };
